import { CSSProperties, HTMLAttributes, KeyboardEvent, PropsWithChildren, memo, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { CalendarEvent } from '../../types';
import { timeToRowNumber } from '../../utils';

interface Props extends Pick<CalendarEvent, 'id' | 'startTime' | 'duration'>, Omit<HTMLAttributes<HTMLElement>, 'id'> {
  style: CSSProperties;
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
}

const EventShellComponent = ({
  children,
  id,
  startTime,
  duration,
  style,
  isSelectable,
  isSelected,
  onSelect,
}: PropsWithChildren<Props>) => {
  const combinedStyle = useMemo(
    () =>
      ({
        '--start-row': timeToRowNumber(startTime),
        '--duration-in-minutes': duration,
        ...style,
      } as CSSProperties),
    [duration, startTime, style]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!isSelectable || !onSelect) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      onSelect();
    },
    [isSelectable, onSelect]
  );

  return (
    <article
      data-event-id={id}
      data-event-selected={isSelected ? 'true' : undefined}
      css={containerStyle}
      style={combinedStyle}
      role={isSelectable ? 'button' : undefined}
      tabIndex={isSelectable ? 0 : undefined}
      onClick={isSelectable ? onSelect : undefined}
      onKeyDown={isSelectable ? handleKeyDown : undefined}
      onMouseDown={(e) => e.stopPropagation()} // to avoid drag create selection from being triggered
    >
      {children}
    </article>
  );
};

export const EventShell = memo(EventShellComponent);

const containerStyle = css({
  gridRow: 'var(--start-row) / span var(--duration-in-minutes)',
  gridColumn: 1,
  overflow: 'visible',
  willChange: 'transform',
  cursor: 'default',
  '&[role="button"]': {
    cursor: 'pointer',
  },
  '> *': {
    height: '100%',
    overflow: 'hidden',
  },
});
